import React, { useRef } from "react";
import ScrollModal from "../ui/ScrollModal.jsx";import guidesConfig from "../../config/guidesConfig";
import { Calculator, ArrowUpRight, Rocket } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import BannerCTA from "../CTA/CTABanner.jsx";
import CTAButtonMob from "../CTA/CTAButtonMob.jsx";
import { ImageInline } from "../GetStarted/GetStartedHelpers.jsx";
import Seo from "../Seo.jsx";
import Tooltip from "./Tooltip";
import "./guides.css";
import mb1 from "../GetStarted/mb1.png";
import mb2 from "../GetStarted/mb2.png";
import vb from "./images/virginwelcome.png";
import muntotvb from './images/muntot-vb.png'
import muntotsm from './images/muntot-sm.png'
import muntotvbb from './images/muntot-vb-bs.png'
import muntotsmb from './images/muntot-sm-bs.png'
import muntotc from './images/muntotc.png'
import bux from './images/smarkets-bucks.png'

const HowToLayABetGuide = () => {
  const meta = guidesConfig.howToLayABetGuide?.seo || {};
  const scrollRef = useRef(null);

  return (
    <div className="guides-content">
      <Seo
        title={meta.title}
        description={meta.description}
        url={guidesConfig.howToLayABetGuide?.route || "/guides"}
      />


      <hr />

      <h2>What Does “Lay a Bet” Mean?</h2>
      <h6 className="info">
        Laying a bet means betting against something happening.
      </h6>
      <p>
        You’re saying “this team <em>won’t</em> win” — the opposite of a normal back
        bet where you say it <em>will</em>. Matched bettors use lay bets to cover
        the other side of a bookmaker offer. It’s how we make sure we don’t lose
        money when chasing free bets.
      </p>
      <div className="grid-fixed g2">
        <div className="col">
        <img src={mb1} alt="How does matched betting work?" />
        <h3>Back bets:</h3>
        <p>
        You place a £10 <span className="back">back</span> bet that Arsenal <em>will</em> win the match. The bookie is laying your bet.
      </p>
        <h6 className="info mb-0">A <span className="back">back</span> bet is a bet that an outcome <em>will</em> happen.</h6>
        </div>
        <div className="col">
        <img src={mb2} alt="How does matched betting work?" />
        <h3>Lay bet:</h3>
        <p>
        You place a <span className="lay">lay</span> bet that Arsenal <em>will not</em> win the match. Another punter is backing your bet.

        </p>
        <h6 className="info mb-0">A <span className="lay">lay</span> bet is a bet that an outcome <em>will not</em> happen.</h6>
        </div>

      </div>
      <hr />

      <table>
        <thead>
          <tr>
            <th>Bet Type</th>
            <th>You’re Betting That…</th>
            <th>If You Win…</th>
            <th>If You Lose…</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Back</td>
            <td>Something <em>will</em> happen</td>
            <td>You win money from the bookie</td>
            <td>You lose your stake</td>
          </tr>
          <tr>
            <td>Lay</td>
            <td>Something <em>won’t</em> happen</td>
            <td>You win the backer’s stake</td>
            <td>
              You pay out their winnings (
              <Tooltip content="The amount you could lose if your lay bet doesn’t win.">
                liability
              </Tooltip>
              )
            </td>
          </tr>
        </tbody>
      </table>


      <h2>Before We Start: Liability & Liquidity</h2>
      <h3>What is liability, and why does it matter?</h3>
      <h6 className="info">
        Liability = The amount you will pay out to your backer if your <span className="lay">lay</span> bet loses.
      </h6>
      <pre>
        <code>Liability = (Lay odds - 1) × Lay stake</code>
        </pre>
        <h4>Example</h4>
        <p>
          Lay £10 on Team A at odds of 3.0<br />
          Liability = (3.0 - 1) × £10 = £20<br />
          If Team A wins, you lose £20.
        </p>
      <p>Liability is taken from your balance when you place the bet and is held by the exchange until the bet settles. It can be alarming when you first see it, but don't worry – if you win, you'll get it back; if you lose, you'll win your <span className="back">back</span> bet.</p>
      <h6 className="warn">Your exchange account needs enough funds to cover this liability, or the bet won’t be accepted.</h6>

      <h2>What is Liquidity?</h2>
      <h6 className="info">Liquidity = Money available in the market to match your bet.</h6>

      <ImageInline
        imageSrc={bux}
        alt="NBA odds example on Smarkets"
        annotations={
            <div>
            <p>Liquidity is shown below the odds for each outcome.</p>
            <div className="example-container">
              <h4>Example</h4>
              <p>If you wanted to <span className="lay">lay</span> £20 on the Milwaukee Bucks at 1.94, you could do so immediately.</p>
              <p>But if you wanted to <span className="lay">lay</span> Miami Heat
              at 2.14, your maximum lay would be £12.</p>
            </div>
            </div>
        }
        />
      <p>
        If liquidity is low, you can:
      </p>
      <ul>
        <li>Move your odds slightly to where there’s more money available</li>
        <li>Wait until closer to kickoff, when more users enter the market</li>
      </ul>
      <p>
        Always check liquidity
        before you click “Lay.”
      </p>
      <p>
        Don’t panic if there's not enough liquidity — liquidity often increases closer to the event.
      </p>

      <BannerCTA 
        to="/guides/betting-exchange-guide" 
        text="Learn How Exchanges Work" 
        icon={ArrowUpRight} 
      />

      <hr />

      <h2>How to Lay a Bet: Step-by-Step</h2>

      <p>
        You’ve just signed up for a new bookmaker, <span className="key">Virgin Bet</span>. They're offering £20 in free bets
        once you place a £10 qualifying bet at odds over 2.0. To trigger the offer with minimal loss,
        you’ll place a <span className="back">back</span> at the bookmaker and a <span className="lay">lay</span> on the exchange to cover all outcomes.
      </p>
      <img src={vb} alt="Virgin bet welcome offer" />
      <h3>Step 1:</h3>
      <p>
        You find a match between <strong>Manchester United vs Tottenham</strong> on Virgin Bet. This seems a potentially good option because:
      </p>
      <ul>
        <li><strong>Soon:</strong> We prefer matches that are happening today, or tomorrow.</li>
        <li><strong>Major event:</strong> There'll be close odds, plenty of <Tooltip content="How much money is available for a market to match your bet">liquidity</Tooltip> at the exchange, and looks like an event a genuine punter would choose.</li>
        <li><strong>Meets Ts and Cs:</strong> The odds are over 2.0.</li>
      </ul>

      <h3>Step 2: Find the Event on the Exchange</h3>
      <p>
        You search for the match — <strong>Manchester United vs Tottenham</strong> — and click into the event page. You’ll
        see the list of outcomes (e.g. Match Odds market: Manchester United, Draw, Tottenham)
        with two columns of prices: the <span className="back">back</span> (green) and the <span className="lay">lay</span> (blue). You want to <strong>lay Manchester United</strong>, so look at the lay column
        next to “Manchester United.”
      </p>

      <h3>Step 3: Check the Lay Odds and Liquidity</h3>
      <div className="grid-fixed g2">
        <div className="col">
            <img src={muntotvb} alt="match odds virgin bet" />
        <p>At Virgin Bet, the <span className="back">back</span> odds for <strong>Manchester United </strong> are 2.44.</p>
        </div>
        <div className="col">
             <img src={muntotsm} alt="match odds smarkets" />
        <p>
          At, Smarkets, the <span className="lay">lay</span> odds for Manchester United to win are 2.48.
        </p>

        </div>
      </div>
      <p>
        There is £571 of
          <Tooltip content="The amount of money available on the exchange to match your bet.">
            liquidity
          </Tooltip>{" "} – more than enough to cover your bet.
      </p>

      <h3>Step 3: Calculate Your Lay Stake</h3>
      <p>
        You don’t just match your back stake blindly — you need to calculate the{" "}
        <span className="lay">lay</span> stake to cancel out your bet. Since your commission at Smarkets is 2%, you enter:
      </p>
      <img src={muntotc} alt="Matched betting calculator" />
      <p>
        The calculator gives you a <strong>lay stake of £9.92</strong>, and your{" "}
        <strong>qualifying loss is 26p</strong>. This is the cost of unlocking your £20 free
        bet.
      </p>

      <CTALarge
        to="/calculators/lay-stake-calculator"
        text="Go to Lay Bet Calculator"
        icon={Calculator}
      />
      <h3>Step 4: Place the Bets</h3>
      <div className="grid-fixed g2">
        <div className="col">
            <img src={muntotvbb} alt="match odds virgin bet" />
        <p>At Virgin Bet, add Manchester united to win to your betslip, and enter a stake of £10.</p>
        <h6 className="warn">Don't place your bet yet.</h6>

        </div>
        <div className="col">
             <img src={muntotsmb} alt="match odds smarkets" />
        <p>
          At, Smarkets, select the odds in the blue column to add Manchester united to win as a <span className="lay">lay</span> bet to your betslip. Enter your stake of £9.92.
        </p>
        <h6 className="warn">Don't place your bet yet.</h6>

        </div>
      </div>
      <h3>Step 5: Confirm the Lay Bet</h3>
      <p>Double-check everything:</p>
      <ul>
      <li>Do you have enough money at Virgin Bet to cover your stake?</li>
      <li>Do you have enough money at Smarkets to cover your <Tooltip content="The amount you could lose if your lay bet doesn’t win.">
            liability
          </Tooltip>?</li>

        <li>Are the odds the same as when you calculated?</li>
        <li>Is your lay stake correct?</li>
        <li>Is there enough liquidity to cover your full stake?</li>
      </ul>

      <p>If yes...</p>
      <p>At Virgin Bet, click <strong>Place bet</strong> to <span className="back">back</span> <strong>Manchester United </strong> at odds of 2.44.</p>
      <p>
          At, Smarkets, click <strong>SELL</strong> to <span className="lay">lay</span> <strong>Manchester United </strong> at odds of 2.48.
        </p>

      <ul>
        <li>
          <strong>If Manchester United win:</strong>
          <br />
          You win the back bet at Virgin Bet: £14.40
          <br />
          You lose your lay bet and pay out £14.68 (your liability).
          <br />
          <strong>Net loss: 28p.</strong>
        </li>
        <li>
          <strong>If Manchester United don’t win:</strong>
          <br />
          You lose your £10 back stake at Virgin Bet.
          <br />
          You win the lay bet and receive the backer’s £9.92 stake.
          <br />
          Minus commission, you keep about £9.72.
          <br />
          <strong>Net loss: 28p.</strong>
        </li>
      </ul>


<h2>You've Now Qualified for Your Free Bet</h2>
<p>
  That’s it — your qualifying bet is complete. You’ve now triggered the bookmaker’s offer and should see your free bet credited soon (this might be within a certain timeframe from after the bet is placed, within a certain timeframe from after the bet is settled, or immediately – check the bookie's terms).
</p>
<p>
  You made a small, controlled loss — in our example, just 28p — but in return you now have a £20 free bet to turn into real profit.
</p>
<p>
  Now it’s time to use that free bet the right way. We’ve written a full step-by-step guide to show you exactly how:
</p>
<BannerCTA
  to="/guides/free-bet-guide"
  text="How to Use a Free Bet (and Lock in Profit)"
  icon={ArrowUpRight}
/>

<ScrollModal scrollContainerRef={scrollRef} props={{ctaKey: "om2"}} />


      <h2>Summary: Key Points to Remember</h2>
      <ul>
        <li>You can only lay bets at exchanges, not bookmakers.</li>
        <li>
          Always use a calculator to get the correct{" "}
          <Tooltip content="The amount you could lose if your lay bet doesn’t win.">
            lay stake
          </Tooltip>
          .
        </li>
        <li>
          Make sure your exchange account has enough to cover{" "}
          <Tooltip content="The amount you could lose if your lay bet doesn’t win.">
            liability
          </Tooltip>
          .
        </li>
        <li>Place both bets quickly to avoid odds movement.</li>
        <li>Liquidity is essential — without it, your lay bet might not be matched.</li>
      </ul>

      <h2>Why Tools Like OddsMonkey Make This Easier</h2>
    <p>
      If all of this sounds like a lot to manage — odds, stakes, liability, commission — that’s because it can be,
      especially when you’re just getting started. That’s where <a href="https://www.oddsMonkey.com/affiliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">OddsMonkey</a> comes in.
    </p>
    <p>
    OddsMonkey helps you:
    </p>
    <ul>
        <li>Find offers to profit from</li>
      <li>Find close back and lay matches automatically</li>
      <li>Calculate correct lay stakes instantly</li>
      <li>Place bets at exchanges with a click</li>
      <li>Track your bets and profit automatically</li>
      <li>Access a support forum which, in our opinion, is worth the fee by itself</li>
    </ul>
    <p>
    You can absolutely do it manually, and many people do — but if you want to save time and reduce errors, it’s worth checking out. They even have a free trial, so you can try it out without committing.
    </p>
    <CTAButtonMob
            to="https://www.oddsmonkey.com/affiliates/affiliate.php?id=66317"
            external='true'
            text="See OddsMonkey for yourself"
            buttonText="Visit"
            icon={Rocket}
            tabText="Free trial available"
        />


    </div>
  );
};

export default HowToLayABetGuide;
